import polygonClipping from 'polygon-clipping'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { GeckoPlateJSON } from 'types/assetModel/features/geckoPlate'
import { GeckoConeCourseJSON } from 'types/assetModel/features/legacyCone/geckoConeCourse'
import { GeckoConicalPlateJSON } from 'types/assetModel/features/legacyCone/geckoConicalPlate'
import { GeckoCourseJSON } from 'types/assetModel/features/verticalTankShell/geckoShellCourse'
import { GeckoConeCircumferentialWeldJSON } from 'types/assetModel/features/welds/geckoConeCircumferentialWeld'
import { GeckoConeRadialWeldJSON } from 'types/assetModel/features/welds/geckoConeRadialWeld'
import { GeckoHorizontalWeldJSON } from 'types/assetModel/features/welds/geckoHorizontalWeld'
import { GeckoVerticalWeldJSON } from 'types/assetModel/features/welds/geckoVerticalWeld'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { xAlongLine, yAlongLine } from 'utils/math'
import { v4 as uuid4 } from 'uuid'

export const outOfBoundsOffset = 1
const tolerance = 1e-3
const precision = 5

export type Position = [number, number]
export type LineSegment = [Position, Position]

type PolyLineSegment = Array<Position>
// [line segment, install date]
export type WeldLineSegment = [LineSegment, string | undefined]
// [line segment, install date]
export type WeldedPolygon = [PolyLineSegment[], string | undefined]

export enum WrappingDirection {
  X = 'x',
  Y = 'y',
  None = 'none',
}

/**
 * Converts courses to WeldedPolygons with an undefined installation date.
 * X coordinates of polygon associated with a course spans {component x start - outOfBoundsOffset} - {component x end + outOfBoundsOffset}.
 * Loads resulting polygons into the @polygonArray
 * @param courses
 * @param componentBounds
 * @param polygonArray
 */
export function convertCoursesToPolygons(
  courses: Array<GeckoCourseJSON> | Array<GeckoConeCourseJSON>,
  componentBounds: GeckoComponentRect,
  polygonArray: Array<WeldedPolygon>
) {
  if (componentBounds.y.unit !== GeckoMeasurementUnit.Meters)
    throw new Error(
      `Component bounds y unit should be in Meters but they are instead in ${componentBounds.y.unit}`
    )

  // Create an array of the courses, that we can then sort
  courses.sort((a, b) => a.feature_geometry.y_end - b.feature_geometry.y_end)

  // Iterate through the sorted courses and generate a new polygon
  let yStart = componentBounds.y.start.num
  const xStart = componentBounds.x.start.num - outOfBoundsOffset
  const xEnd = componentBounds.x.end.num + outOfBoundsOffset

  courses.forEach((course) => {
    const yEnd = course.feature_geometry.y_end
    polygonArray.push([
      [
        [
          [xStart, yStart],
          [xEnd, yStart],
          [xEnd, yEnd],
          [xStart, yEnd],
          [xStart, yStart],
        ],
      ],
      undefined,
    ])
    yStart = yEnd
  })
}

/**
 * Converts plates into WeldedPolygons with respective installation date.
 * Wrapped plates are split into two polygons where the edges that
 * wrap are placed outside the asset by an offset of outOfBoundsOffset, so long
 * as the wrap direction of the plate agrees with the @wrapDirection.
 * Plates that wrap in a direction that disagrees with the @wrapDirection
 * will cause an error to be thrown.
 * Resulting polygons are loaded into the @polygonArray
 * @param plates
 * @param componentBounds
 * @param wrapDirection
 * @param polygonArray
 */
export function convertPlatesToPolygons(
  plates: Array<GeckoPlateJSON> | Array<GeckoConicalPlateJSON>,
  componentBounds: GeckoComponentRect,
  wrapDirection: WrappingDirection,
  polygonArray: Array<WeldedPolygon>
) {
  // Sort the plates in order of installation, assuming really old if none present
  plates.sort((a, b) =>
    (a.installation_date ?? '0000-00-00').localeCompare(
      b.installation_date ?? '0000-00-00'
    )
  )

  plates.forEach((plate) => {
    const geometry = plate.feature_geometry
    if (geometry.x_start >= geometry.x_end) {
      if (wrapDirection !== WrappingDirection.X)
        throw new Error(`component ${componentBounds.componentID} 
      has a plate {${plate.name}} wrapping in the x direction but doesn't 
      itself wrap in the x driection`)
      const newXEnd = componentBounds.x.end.num + outOfBoundsOffset
      const newXStart = componentBounds.x.start.num - outOfBoundsOffset
      polygonArray.push([
        [
          [
            [geometry.x_start, geometry.y_start],
            [newXEnd, geometry.y_start],
            [newXEnd, geometry.y_end],
            [geometry.x_start, geometry.y_end],
            [geometry.x_start, geometry.y_start],
          ],
        ],
        plate.installation_date,
      ])

      polygonArray.push([
        [
          [
            [newXStart, geometry.y_start],
            [geometry.x_end, geometry.y_start],
            [geometry.x_end, geometry.y_end],
            [newXStart, geometry.y_end],
            [newXStart, geometry.y_start],
          ],
        ],
        plate.installation_date,
      ])
    } else if (geometry.y_start >= geometry.y_end) {
      if (wrapDirection !== WrappingDirection.Y)
        throw new Error(`component ${componentBounds.componentID} 
      has a plate {${plate.name}} wrapping in the y direction but doesn't 
      itself wrap in the y driection`)
      const newYEnd = componentBounds.y.end.num + outOfBoundsOffset
      const newYStart = componentBounds.y.start.num - outOfBoundsOffset
      polygonArray.push([
        [
          [
            [geometry.x_start, geometry.y_start],
            [geometry.x_end, geometry.y_start],
            [geometry.x_end, newYEnd],
            [geometry.x_start, newYEnd],
            [geometry.x_start, geometry.y_start],
          ],
        ],
        plate.installation_date,
      ])
      polygonArray.push([
        [
          [
            [geometry.x_start, newYStart],
            [geometry.x_end, newYStart],
            [geometry.x_end, geometry.y_end],
            [geometry.x_start, geometry.y_end],
            [geometry.x_start, newYStart],
          ],
        ],
        plate.installation_date,
      ])
    } else {
      polygonArray.push([
        [
          [
            [geometry.x_start, geometry.y_start],
            [geometry.x_end, geometry.y_start],
            [geometry.x_end, geometry.y_end],
            [geometry.x_start, geometry.y_end],
            [geometry.x_start, geometry.y_start],
          ],
        ],
        plate.installation_date,
      ])
    }
  })
}

/**
 * A function to calculate the overlap of polygons in a list. Accepts a list of
 * polygons, carves the polygons to remove any overlap, and
 * returns a map of resulting welds.
 * Assumes polygon coordinates are in the same units as component bounds.
 * @param sortedPolygons      An array of WeldedPolygons associated to component
 * @param componentBounds     Bounds of respective component
 * @param wrapDirection       Direction that component wraps
 */
export function convertPolygonsToWeldMap(
  polygons: Array<WeldedPolygon>,
  componentBounds: GeckoComponentRect,
  wrapDirection: WrappingDirection
): Map<string, GeckoHorizontalWeldJSON | GeckoVerticalWeldJSON> {
  if (polygons.length === 0)
    return new Map<string, GeckoHorizontalWeldJSON | GeckoVerticalWeldJSON>()
  sortPolygonsByInstallationDate(polygons)

  const modifiedPolygons: Array<WeldedPolygon> = []

  // Iterate through each polygon in our sorted list
  for (let i = 0; i < polygons.length; i++) {
    let p = polygons[i]

    for (let j = i + 1; j < polygons.length; j++) {
      // cut out all other polygons from current polygon
      if (!p.length) break
      p = [polygonClipping.difference(p[0], polygons[j][0]).flat(), p[1]] // we can flatten because we don't care about inner rings right now
    }
    if (!p) continue

    modifiedPolygons.push(p)
  }

  const lineArray: Array<WeldLineSegment> | undefined =
    convertPolygonsToLines(modifiedPolygons)
  modifiedPolygons.length = 0 // deallocate

  const nonOverlappingLines = filterOverlappingWelds(lineArray)
  lineArray.length = 0 //deallocate

  const weldsInBounds = removeWeldsOutsideComponentBounds(
    nonOverlappingLines,
    componentBounds
  )
  nonOverlappingLines.length = 0

  const convergedWelds = convergeWeldsThatOverlapAtBoundary(
    weldsInBounds,
    componentBounds,
    wrapDirection
  )

  const weldJSONs = convergedWelds.map((line) =>
    generateWeldFromPosition(line[0], line[1])
  )

  return new Map(
    weldJSONs.map((json) => [
      (json.type === GeckoFeatureModelType.VerticalWeld ? 'vw' : 'hw') +
        uuid4().slice(0, 3),
      json,
    ])
  )
}

/**
 * A function to calculate the overlap of cone polygons in a list. Accepts a list of
 * polygons, carves the polygons to remove any overlap, and
 * returns a map of resulting welds.
 * Assumes polygon coordinates are in the same units as component bounds.
 * @param sortedPolygons      An array of WeldedPolygons associated to component
 * @param componentBounds     Bounds of respective component
 * @param wrapDirection       Direction that component wraps
 */
export function convertConePolygonsToWeldMap(
  polygons: Array<WeldedPolygon>,
  componentBounds: GeckoComponentRect,
  wrapDirection: WrappingDirection
): Map<string, GeckoConeRadialWeldJSON | GeckoConeCircumferentialWeldJSON> {
  if (polygons.length === 0)
    return new Map<
      string,
      GeckoConeRadialWeldJSON | GeckoConeCircumferentialWeldJSON
    >()
  sortPolygonsByInstallationDate(polygons)

  const modifiedPolygons: Array<WeldedPolygon> = []

  // Iterate through each polygon in our sorted list
  for (let i = 0; i < polygons.length; i++) {
    let p = polygons[i]

    for (let j = i + 1; j < polygons.length; j++) {
      // cut out all other polygons from current polygon
      if (!p.length) break
      p = [polygonClipping.difference(p[0], polygons[j][0]).flat(), p[1]] // we can flatten because we don't care about inner rings right now
    }
    if (!p) continue

    modifiedPolygons.push(p)
  }

  const lineArray: Array<WeldLineSegment> | undefined =
    convertPolygonsToLines(modifiedPolygons)
  modifiedPolygons.length = 0 // deallocate

  const nonOverlappingLines = filterOverlappingWelds(lineArray)
  lineArray.length = 0 //deallocate

  const weldsInBounds = removeWeldsOutsideComponentBounds(
    nonOverlappingLines,
    componentBounds
  )
  nonOverlappingLines.length = 0

  const convergedWelds = convergeWeldsThatOverlapAtBoundary(
    weldsInBounds,
    componentBounds,
    wrapDirection
  )

  const weldJSONs = convergedWelds.map((line) =>
    generateConeWeldFromPosition(line[0], line[1])
  )

  return new Map(
    weldJSONs.map((json) => [
      (json.type === GeckoFeatureModelType.ConeRadialWeld ? 'vw' : 'hw') +
        uuid4().slice(0, 3),
      json,
    ])
  )
}

/** START OF INTERNAL FUNCTIONS. EXPORTED FOR TESTING PURPOSES */

/**
 * Internal function.
 * Orders line end points by ascending x, then ascending y.
 * @param polygons - ordered by ascending importance, newer plates appear later
 * @returns
 */
export function convertPolygonsToLines(
  polygons: Array<WeldedPolygon>
): Array<WeldLineSegment> {
  const lineArray: Array<WeldLineSegment> = []

  for (let i = polygons.length - 1; i >= 0; i--) {
    const [p, installDate] = polygons[i]
    for (let pi = 0; pi < p.length; pi++) {
      // iterate through each linear ring in p
      const linearRing = p[pi]
      for (let ci = 0; ci < linearRing.length - 1; ) {
        // iterate through each line segment in a linear ring
        let s = linearRing[ci++]
        let t = linearRing[ci]
        if (s[0] > t[0] || (s[0] === t[0] && s[1] > t[1])) {
          // order points
          ;[s, t] = [t, s]
        }
        const sx = assetModelRounding(s[0])
        const sy = assetModelRounding(s[1])
        const tx = assetModelRounding(t[0])
        const ty = assetModelRounding(t[1])
        lineArray.push([
          [
            [sx, sy],
            [tx, ty],
          ],
          installDate,
        ])
      }
    }
  }
  return lineArray
}

/**
 * Internal function.
 * Function to converge weld lines that have the same install date, overlap, and
 * are parallel, and trim or remove weld lines that are covered by a weld with a
 * later install date.
 * @param welds     An object containing all the welds to be considered when
 *                  looking for overlap
 */
export function filterOverlappingWelds(
  welds: Array<WeldLineSegment>
): Array<LineSegment> {
  if (welds.length === 0) return []
  sortWeldsByInstallationDate(welds, true)
  const weldsOverlapsRemoved: Array<WeldLineSegment> = []
  weldsOverlapsRemoved.push(welds[0])
  const currentWeldSegments: Array<WeldLineSegment | undefined> = []
  for (let i = 1; i < welds.length; i++) {
    currentWeldSegments.push(welds[i])
    for (let j = 0; j < weldsOverlapsRemoved.length; j++) {
      const weldLineB = weldsOverlapsRemoved[j] // occured earlier
      const iterLength = currentWeldSegments.length
      for (let k = 0; k < iterLength; k++) {
        const weldLineA = currentWeldSegments[k]
        if (!weldLineA) continue
        const [lineA, installDateA] = weldLineA
        const [lineB, installDateB] = weldLineB

        if (!areParallel(lineA, lineB)) {
          continue
        }

        const A1OnLineB = isPointOnLine(
          ...lineA[0],
          ...lineB[0],
          ...lineB[1],
          tolerance
        )
        const A2OnLineB = isPointOnLine(
          ...lineA[1],
          ...lineB[0],
          ...lineB[1],
          tolerance
        )
        const B1OnLineA = isPointOnLine(
          ...lineB[0],
          ...lineA[0],
          ...lineA[1],
          tolerance
        )
        const B2OnLineA = isPointOnLine(
          ...lineB[1],
          ...lineA[0],
          ...lineA[1],
          tolerance
        )

        if (!A1OnLineB && !A2OnLineB && !B1OnLineA && !B2OnLineA) {
          continue
        } else if (A1OnLineB && A2OnLineB) {
          if (installDateA === installDateB) {
            weldsOverlapsRemoved[j] = [[lineB[0], lineB[1]], installDateB]
            currentWeldSegments[k] = undefined
          } else {
            currentWeldSegments[k] = undefined
          }
        } else if (B1OnLineA && B2OnLineA) {
          if (installDateA === installDateB) {
            weldsOverlapsRemoved[j] = [[lineA[0], lineA[1]], installDateB]
            currentWeldSegments[k] = undefined
          } else {
            currentWeldSegments[k] = [[lineA[0], lineB[0]], installDateA]
            currentWeldSegments.push([[lineB[1], lineA[1]], installDateA])
          }
        } else if (B2OnLineA && A1OnLineB) {
          if (installDateA === installDateB) {
            weldsOverlapsRemoved[j] = [[lineB[0], lineA[1]], installDateB]
            currentWeldSegments[k] = undefined
          } else {
            currentWeldSegments[k] = [[lineB[1], lineA[1]], installDateA]
          }
        } else if (B1OnLineA && A2OnLineB) {
          if (installDateA === installDateB) {
            weldsOverlapsRemoved[j] = [[lineA[0], lineB[1]], installDateB]
            currentWeldSegments[k] = undefined
          } else {
            currentWeldSegments[k] = [[lineA[0], lineB[0]], installDateA]
          }
        }
      }
    }

    while (currentWeldSegments.length > 0) {
      const weld = currentWeldSegments.pop()
      if (weld && length(weld[0]) > tolerance) {
        weldsOverlapsRemoved.push(weld)
      }
    }
  }
  return weldsOverlapsRemoved.map((weld) => weld[0])
}

/**
 * Internal Function
 * Removes welds that fall outside the component bounds. Keeps any weld that has
 * a non zero length in bounds. Assumes welds are in the same measurement
 * units as the component bounds.
 * @param weldArray
 * @param componentBounds
 */
export function removeWeldsOutsideComponentBounds(
  weldArray: Array<LineSegment>,
  componentBounds: GeckoComponentRect
) {
  const weldsInBounds: Array<LineSegment> = []

  for (let i = 0; i < weldArray.length; i++) {
    const [start, end] = weldArray[i]

    const minX = Math.min(start[0], end[0])
    const maxX = Math.max(start[0], end[0])
    if (
      minX + tolerance < componentBounds.x.start.num &&
      maxX <= componentBounds.x.start.num
    )
      continue
    if (
      minX >= componentBounds.x.end.num &&
      maxX - tolerance > componentBounds.x.end.num
    )
      continue

    const minY = Math.min(start[1], end[1])
    const maxY = Math.max(start[1], end[1])
    if (
      minY + tolerance < componentBounds.y.start.num &&
      maxY <= componentBounds.y.start.num
    )
      continue
    if (
      minY >= componentBounds.y.end.num &&
      maxY - tolerance > componentBounds.y.end.num
    )
      continue
    weldsInBounds.push([start, end])
  }

  return weldsInBounds
}

/**
 * Internal Function
 * Converge welds that overlap at boundary, or truncate welds to edge.
 * Assumes
 * @param nonOverlappingWeldArray
 * @param componentBounds
 * @param wrapDirection
 * @returns
 */
export function convergeWeldsThatOverlapAtBoundary(
  nonOverlappingWeldArray: Array<LineSegment>,
  componentBounds: GeckoComponentRect,
  wrapDirection: WrappingDirection
): Array<LineSegment> {
  const xStart = assetModelRounding(componentBounds.x.start.num)
  const xEnd = assetModelRounding(componentBounds.x.end.num)
  const yStart = assetModelRounding(componentBounds.y.start.num)
  const yEnd = assetModelRounding(componentBounds.y.end.num)
  // store portions of wrapped welds so we can connect them later
  // we will store as string represented as a string formatted as,
  // the wrapped end point and the slope (rounded to 5 sig figs) specifically "(x, y) slope"
  const mergedWelds: Array<[Position, Position]> = []
  const unmergedWelds: [Position, Position][] = []
  const unmergedWeldSlopes: number[] = []
  const unmergedWeldMergePoints: [number, number][] = []
  for (let i = 0; i < nonOverlappingWeldArray.length; i++) {
    let [point1, point2] = nonOverlappingWeldArray[i]
    let [p1x, p1y] = point1
    let [p2x, p2y] = point2
    if (p1x < xStart !== p2x > xEnd && p1y < yStart !== p2y > yEnd) {
      throw new Error('Welds that wrap in both directions shouldnt be possible')
    } else if (Math.min(p1x, p2x) < xStart !== Math.max(p1x, p2x) > xEnd) {
      if (wrapDirection !== WrappingDirection.X) {
        console.error(
          'Weld wrapped in x direction for an asset that does not wrap in x direction'
        )
        mergedWelds.push([point1, point2])
        continue
      }
      if (p2x < p1x) {
        ;[p1x, p2x] = [p2x, p1x]
        ;[p1y, p2y] = [p2y, p1y]
        ;[point1, point2] = [point2, point1]
      }
      const xToEval = p1x < xStart ? xStart : xEnd
      const yAtBoundary = yAlongLine(point1, point2, xToEval)
      const slope = calcSlope([point1, point2])
      const partnerX = p1x < xStart ? xEnd : xStart
      const partnerIndex = findUnmergedPartnerIndex(
        partnerX,
        yAtBoundary,
        slope,
        unmergedWeldSlopes,
        unmergedWeldMergePoints
      )
      if (
        partnerIndex >= 0 &&
        !(p1x < xStart
          ? arePointsEqual(point2, unmergedWelds[partnerIndex][0])
          : arePointsEqual(point1, unmergedWelds[partnerIndex][1]))
      ) {
        const partner = unmergedWelds[partnerIndex]
        mergedWelds.push(
          p1x < xStart ? [partner[0], point2] : [point1, partner[1]]
        )
        unmergedWelds.splice(partnerIndex, 1)
        unmergedWeldSlopes.splice(partnerIndex, 1)
        unmergedWeldMergePoints.splice(partnerIndex, 1)
      } else {
        unmergedWelds.push(
          p1x < xStart
            ? [[xStart, yAtBoundary], point2]
            : [point1, [xEnd, yAtBoundary]]
        )
        unmergedWeldSlopes.push(slope)
        unmergedWeldMergePoints.push([xToEval, yAtBoundary])
      }
    } else if (Math.min(p1y, p2y) < yStart !== Math.max(p1y, p2y) > yEnd) {
      if (wrapDirection !== WrappingDirection.Y) {
        console.error(
          'Weld wrapped in y direction for an asset that does not wrap in y direction'
        )
        mergedWelds.push([point1, point2])
        continue
      }
      if (p2y < p1y) {
        ;[p1x, p2x] = [p2x, p1x]
        ;[p1y, p2y] = [p2y, p1y]
        ;[point1, point2] = [point2, point1]
      }
      const yToEval = p1y < yStart ? yStart : yEnd
      const xAtBoundary = xAlongLine(point1, point2, yToEval)
      const slope = calcSlope([point1, point2])
      const partnerY = p1y < yStart ? yEnd : yStart
      const partnerIndex = findUnmergedPartnerIndex(
        xAtBoundary,
        partnerY,
        slope,
        unmergedWeldSlopes,
        unmergedWeldMergePoints
      )
      if (
        partnerIndex >= 0 &&
        !(p1y < yStart
          ? arePointsEqual(point2, unmergedWelds[partnerIndex][0])
          : arePointsEqual(point1, unmergedWelds[partnerIndex][1]))
      ) {
        const partner = unmergedWelds[partnerIndex]
        mergedWelds.push(
          p1y < yStart ? [partner[0], point2] : [point1, partner[1]]
        )
        //myArray.splice(3, 1); // Removes one item, at index 3

        unmergedWelds.splice(partnerIndex, 1)
        unmergedWeldSlopes.splice(partnerIndex, 1)
        unmergedWeldMergePoints.splice(partnerIndex, 1)
      } else {
        unmergedWelds.push(
          p1y < yStart
            ? [[xAtBoundary, yStart], point2]
            : [point1, [xAtBoundary, yEnd]]
        )
        unmergedWeldSlopes.push(slope)
        unmergedWeldMergePoints.push([xAtBoundary, yToEval])
      }
    } else mergedWelds.push([point1, point2])
  }

  mergedWelds.push(...unmergedWelds)

  const truncatedConvergedWelds: Array<LineSegment> = mergedWelds.map(
    (line) => {
      const x1 = Math.max(Math.min(line[0][0], xEnd), xStart)
      const x2 = Math.max(Math.min(line[1][0], xEnd), xStart)
      const y1 = Math.max(Math.min(line[0][1], yEnd), yStart)
      const y2 = Math.max(Math.min(line[1][1], yEnd), yStart)
      return [
        [x1, y1],
        [x2, y2],
      ]
    }
  )

  return truncatedConvergedWelds
}

/**
 * Internal Function
 * Function to create the Asset Model JSON for a Horizontal Weld or Vertical Weld.
 * To be stored in entry. Wrapped welds will have a starting position greater
 * than the ending position
 * @param pointA    The starting point for a weld (x, y position)
 * @param pointB    The ending point for a weld (x, y position)
 */
export function generateWeldFromPosition(
  pointA: Position,
  pointB: Position
): GeckoVerticalWeldJSON | GeckoHorizontalWeldJSON {
  if (
    Math.abs(pointA[0] - pointB[0]) > tolerance &&
    Math.abs(pointA[1] - pointB[1]) > tolerance
  ) {
    throw Error(
      'Unable to build a horizontal or vertical weld from these positions!'
    )
  }

  if (Math.abs(pointA[0] - pointB[0]) <= tolerance) {
    // Vertical Weld (both x values are the same)
    return {
      name: undefined,
      type: GeckoFeatureModelType.VerticalWeld,
      feature_geometry: {
        y_start: pointA[1],
        y_end: pointB[1],
        x: pointA[0],
      },
    }
  } else if (Math.abs(pointA[1] - pointB[1]) <= tolerance) {
    // Horizontal Weld (both y values are the same)
    return {
      name: undefined,
      type: GeckoFeatureModelType.HorizontalWeld,
      feature_geometry: {
        x_start: pointA[0],
        x_end: pointB[0],
        y: pointA[1],
      },
    }
  } else {
    throw Error('Unexpected state! Unable to generate a weld from this data!')
  }
}

/**
 * Internal Function
 * Function to create the Asset Model JSON for a Cone Circumferential Weld or Cone Radial Weld.
 * To be stored in entry. Wrapped welds will have a starting position greater
 * than the ending position
 * @param pointA    The starting point for a weld (x, y position)
 * @param pointB    The ending point for a weld (x, y position)
 */
export function generateConeWeldFromPosition(
  pointA: Position,
  pointB: Position
): GeckoConeCircumferentialWeldJSON | GeckoConeRadialWeldJSON {
  if (
    Math.abs(pointA[0] - pointB[0]) > tolerance &&
    Math.abs(pointA[1] - pointB[1]) > tolerance
  ) {
    throw Error(
      'Unable to build a horizontal or vertical weld from these positions!'
    )
  }

  if (Math.abs(pointA[0] - pointB[0]) <= tolerance) {
    // Vertical Weld (both x values are the same)
    return {
      name: undefined,
      type: GeckoFeatureModelType.ConeRadialWeld,
      feature_geometry: {
        y_start: pointA[1],
        y_end: pointB[1],
        x: pointA[0],
      },
    }
  } else if (Math.abs(pointA[1] - pointB[1]) <= tolerance) {
    // Horizontal Weld (both y values are the same)
    return {
      name: undefined,
      type: GeckoFeatureModelType.ConeCircumferentialWeld,
      feature_geometry: {
        x_start: pointA[0],
        x_end: pointB[0],
        y: pointA[1],
      },
    }
  } else {
    throw Error('Unexpected state! Unable to generate a weld from this data!')
  }
}

/*
 * Finds index of the unmerged partner of a wrapped weld, with some tolerance.
 * Returns -1 if not found.
 * @param partnerX
 * @param partnerY
 * @param partnerSlope
 * @param unmergedWeldSlopes
 * @param unmergedWeldsMergePoint
 * @returns
 */
function findUnmergedPartnerIndex(
  partnerX: number,
  partnerY: number,
  partnerSlope: number,
  unmergedWeldSlopes: number[],
  unmergedWeldsMergePoint: [number, number][]
): number {
  const partnerIndex = unmergedWeldSlopes.findIndex((slope, i) => {
    const equalSlopes =
      Math.abs(slope - partnerSlope) < tolerance ||
      (Number.isNaN(slope) && Number.isNaN(partnerSlope))
    const equalX =
      Math.abs(partnerX - unmergedWeldsMergePoint[i][0]) < tolerance
    const equalY =
      Math.abs(partnerY - unmergedWeldsMergePoint[i][1]) < tolerance
    return equalSlopes && equalX && equalY
  })
  return partnerIndex
}

/**
 * Calculates length of line.
 * @param line
 * @returns
 */
function length(line: LineSegment) {
  return Math.sqrt(
    Math.pow(line[0][0] - line[1][0], 2) + Math.pow(line[0][1] - line[1][1], 2)
  )
}

/**
 * Given an x value within the range of a line segment, calculates the associated
 * y value along the line. If x is out of range, returns undefined.
 * @param line
 * @param x
 * @returns absolute value of slope, as a string, to precision set by precision;
 *          if
 */
function calcSlope(line: LineSegment): number {
  let [p1, p2] = line
  if (p1[0] > p2[0] || p1[0] === p2[0] || p1[1] > p2[1]) {
    ;[p2, p1] = [p1, p2]
  }
  return p2[0] === p1[0] ? Number.NaN : (p2[1] - p1[1]) / (p2[0] - p1[0])
}
/**
 * If greater than or equal to 1, round by precision digits; else
 *  n to precision precision digits.
 * @param n
 * @returns
 */
function assetModelRounding(n: number) {
  return n >= 0
    ? Number(n.toFixed(precision))
    : Number(n.toPrecision(precision))
}
/**
 * Returns if a and b are the same coordinate, with some tolerance
 * @param a
 * @param b
 * @returns
 */
function arePointsEqual(a: Position, b: Position) {
  return Math.abs(a[0] - b[0]) < tolerance && Math.abs(a[1] - b[1]) < tolerance
}

/**
 * Returns whether line segments A and B are parallel.
 * @param a Line a
 * @param b Line b
 * @returns
 */
const areParallel = (a: LineSegment, b: LineSegment): boolean => {
  const ax = a[0][0] - a[1][0]
  const ay = a[0][1] - a[1][1]
  const bx = b[0][0] - b[1][0]
  const by = b[0][1] - b[1][1]
  const d = ax * by - ay * bx
  return Math.abs(d) <= tolerance
}

/**
 * Returns whether coordinate c is on line l, inclusive of endpoints,
 *  with some tolerance
 * @param line
 * @param c
 * @returns
 */
export const isPointOnLine = (
  px: number,
  py: number,
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  tolerance: number
): boolean => {
  const ax = px - sx
  const ay = py - sy
  const lx = ex - sx
  const ly = ey - sy

  const d = ax * ly - ay * lx
  return (
    Math.abs(d) <= tolerance &&
    px + tolerance >= Math.min(sx, ex) &&
    px - tolerance <= Math.max(sx, ex) &&
    py + tolerance >= Math.min(sy, ey) &&
    py - tolerance <= Math.max(sy, ey)
  )
}

/**
 * Sort weld array by installation date. If installation dates are
 * equivalent, then sort by the starting points x value and secondly
 * the starting points y value.
 * Undefined installation dates predate any defined installation date.
 * @param array
 * @param reverse
 */
function sortWeldsByInstallationDate(
  array: Array<WeldLineSegment>,
  reverse: boolean = false
) {
  array.sort((a, b) => {
    const [lineA, installDateA] = a
    const [lineB, installDateB] = b
    const stringComp = (installDateA ?? '0000-00-00').localeCompare(
      installDateB ?? '0000-00-00'
    )
    if (!stringComp) {
      const [ax1, ay1] = lineA[0]
      const [bx1, by1] = lineB[0]
      const [ax2, ay2] = lineA[1]
      const [bx2, by2] = lineB[1]
      return (
        (reverse ? -1 : 1) *
        (ax1 === bx1
          ? ax2 === bx2
            ? by1 === ay1
              ? by2 - ay2
              : by1 - ay1
            : bx2 - ax2
          : bx1 - ax1)
      )
    }
    return (reverse ? -1 : 1) * stringComp
  })
}

/**
 * Sort welded polygon array by installation date.
 * Undefined installation dates predate any defined installation date.
 * @param array
 * @param reverse
 */
function sortPolygonsByInstallationDate(
  array: Array<WeldedPolygon>,
  reverse: boolean = false
) {
  array.sort((a, b) => {
    const installDateA = a[1]
    const installDateB = b[1]
    const stringComp = (installDateA ?? '0000-00-00').localeCompare(
      installDateB ?? '0000-00-00'
    )
    return (reverse ? -1 : 1) * stringComp
  })
}
